import { useEffect, useMemo, useRef, useState } from 'react'
import { Printer, ArrowLeft, ClipboardList, Download, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/i18n'
import { IntuiEmptyState } from '@/components/intueri/IntuiEmptyState'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import {
  IntuiSelect,
  IntuiSelectContent,
  IntuiSelectItem,
  IntuiSelectTrigger,
  IntuiSelectValue,
} from '@/components/intueri/IntuiSelect'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import SquareLoader from '../SquareLoader'
import { WorkflowResultPanel } from '../WorkflowResultPanel'
import { listWorkflowRuns } from '../workflowApi'
import type { WorkflowRunView } from '../workflowTypes'
import {
  downloadTextFile,
  formatReportDate,
  reportMarkdown,
  reportStatusPill,
  runTypeLabel,
  type ReportStatusTone,
} from '../reportUtils'

const PILL_CLASSES: Record<ReportStatusTone, string> = {
  clean: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400',
  attention: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  failed: 'bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400',
  running: 'bg-muted text-muted-foreground',
}

function StatusPill({ run }: { run: WorkflowRunView }) {
  const pill = reportStatusPill(run)
  return (
    <span className={cn('inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium', PILL_CLASSES[pill.tone])}>
      {pill.label}
    </span>
  )
}

/**
 * The workflow's Report tab (Figma "8. Report"): run history (Report date ·
 * Run type · Status) with search + sort, and a drill-in detail (summary
 * sections + Markdown download). Runs are fetched server-side filtered by
 * templateId; the client-side filter below is a cheap invariant guard.
 */
export default function WorkflowReportView({
  templateId,
  initialRunId,
  onViewRun,
}: {
  templateId: string
  /**
   * Open straight into THIS run's detail instead of the list. Set from
   * `?reportRunId=` when the user came from a specific run's result card, so the
   * report shows the run they clicked rather than the most recent one.
   */
  initialRunId?: string
  /**
   * Open THIS run in the Build view (Figma "10. Update" report → View Run).
   * The builder loads a specific run from `?runId=`, so the parent just swaps
   * the query param — the run's canvas + chat (and its parked clarification,
   * if any) rehydrate, letting the user inspect or resume where it stopped.
   */
  onViewRun?: (runId: string) => void
}) {
  const { t } = useTranslation()
  const [runs, setRuns] = useState<WorkflowRunView[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest')
  // Spec §11: the report is framed around the two run SOURCES (Schedule vs
  // Manual) — filterable, not just a display column.
  const [source, setSource] = useState<'all' | 'scheduled' | 'manual'>('all')
  const [selected, setSelected] = useState<WorkflowRunView | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    setLoadFailed(false)
    listWorkflowRuns({ templateId, limit: 100 })
      .then(({ runs: all }) => {
        if (!active) return
        // Server-side filter; keep the client check as a cheap invariant.
        setRuns((all ?? []).filter((run) => run.templateId === templateId))
      })
      .catch(() => {
        // A fetch error is NOT an empty history — surface it as its own state
        // instead of the misleading "Run the workflow to generate its first
        // report" empty card.
        if (active) {
          setRuns([])
          setLoadFailed(true)
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [templateId])

  // Deep-link: land on the requested run once the history arrives. Keyed on the id
  // so returning to the list (selected → null) doesn't bounce the user back in.
  const deepLinkedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!initialRunId || deepLinkedRef.current === initialRunId) return
    const match = runs.find((run) => run.id === initialRunId)
    if (!match) return
    deepLinkedRef.current = initialRunId
    setSelected(match)
  }, [initialRunId, runs])

  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase()
    const ms = (run: WorkflowRunView) =>
      new Date(run.startedAt ?? run.createdAt ?? 0).getTime()
    const filtered = runs.filter((run) => {
      if (source !== 'all') {
        const scheduled = runTypeLabel(run) === 'Scheduled'
        if (source === 'scheduled' ? !scheduled : scheduled) return false
      }
      if (!q) return true
      return (
        formatReportDate(run.startedAt).toLowerCase().includes(q) ||
        reportStatusPill(run).label.toLowerCase().includes(q) ||
        runTypeLabel(run).toLowerCase().includes(q)
      )
    })
    return [...filtered].sort((a, b) => (sort === 'newest' ? ms(b) - ms(a) : ms(a) - ms(b)))
  }, [runs, search, sort, source])

  if (selected) {
    const pill = reportStatusPill(selected)
    return (
      <div className="flex h-full min-h-0 flex-col overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4">
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="flex items-center gap-1.5 text-sm text-foreground hover:text-muted-foreground"
          >
            <ArrowLeft className="size-4" />
            {t('common:back', { defaultValue: 'Back' })}
          </button>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t('workflow:report.print', { defaultValue: 'Print / save as PDF' })}
              onClick={() => window.print()}
              className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Printer className="size-4" />
            </button>
            <button
              type="button"
              aria-label={t('workflow:report.download', { defaultValue: 'Download report (Markdown)' })}
              onClick={() =>
                downloadTextFile(
                  `workflow-report-${(selected.startedAt ?? selected.id).slice(0, 10)}.md`,
                  reportMarkdown(selected)
                )
              }
              className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Download className="size-4" />
            </button>
          </div>
        </div>
        {/* Figma 8-report detail: content starts at the left page padding,
            full width — not a centered column. */}
        <div className="w-full px-6 pb-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-foreground">
                {formatReportDate(selected.startedAt)}
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <StatusPill run={selected} />
                <span className="text-sm text-foreground">
                  {runTypeLabel(selected) === 'Scheduled'
                    ? t('workflow:report.scheduled_run', { defaultValue: 'Scheduled Run' })
                    : t('workflow:report.manual_run', { defaultValue: 'Manual Run' })}
                </span>
              </div>
            </div>
            {onViewRun && (
              <button
                type="button"
                onClick={() => onViewRun(selected.id)}
                className="inline-flex shrink-0 items-center rounded-lg border border-input bg-background px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                {t('workflow:report.view_run', { defaultValue: 'View Run' })}
              </button>
            )}
          </div>
          <div className="mt-6">
            {selected.finalOutput ? (
              <WorkflowResultPanel run={selected} />
            ) : (
              <p className="text-sm text-muted-foreground">
                {pill.tone === 'failed'
                  ? t('workflow:report.no_output_failed', {
                      defaultValue: 'This run failed before producing a report.',
                    })
                  : t('workflow:report.no_output_pending', {
                      defaultValue: 'This run has not produced a report yet.',
                    })}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-6">
      <div className="flex shrink-0 items-center gap-3">
        <div className="relative w-full sm:w-[280px]">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <IntuiInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('workflow:search', { defaultValue: 'Search' })}
            className="h-9 ps-9"
          />
        </div>
        <IntuiSelect value={source} onValueChange={(v) => setSource(v as typeof source)}>
          <IntuiSelectTrigger className="h-9 w-[170px]">
            <IntuiSelectValue />
          </IntuiSelectTrigger>
          <IntuiSelectContent>
            <IntuiSelectItem value="all">
              {t('workflow:report.source_all', { defaultValue: 'All runs' })}
            </IntuiSelectItem>
            <IntuiSelectItem value="scheduled">
              {t('workflow:report.source_scheduled', { defaultValue: 'Scheduled runs' })}
            </IntuiSelectItem>
            <IntuiSelectItem value="manual">
              {t('workflow:report.source_manual', { defaultValue: 'Manual runs' })}
            </IntuiSelectItem>
          </IntuiSelectContent>
        </IntuiSelect>
        <IntuiSelect value={sort} onValueChange={(v) => setSort(v as 'newest' | 'oldest')}>
          <IntuiSelectTrigger className="h-9 w-[190px]">
            <IntuiSelectValue />
          </IntuiSelectTrigger>
          <IntuiSelectContent>
            <IntuiSelectItem value="newest">
              {t('workflow:report.sort_newest', { defaultValue: 'Newest first' })}
            </IntuiSelectItem>
            <IntuiSelectItem value="oldest">
              {t('workflow:report.sort_oldest', { defaultValue: 'Oldest first' })}
            </IntuiSelectItem>
          </IntuiSelectContent>
        </IntuiSelect>
      </div>

      {loading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <SquareLoader label={t('workflow:report.loading', { defaultValue: 'Loading reports…' })} />
        </div>
      ) : loadFailed ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <IntuiEmptyState
            icon={<ClipboardList className="size-4 text-muted-foreground" />}
            title={t('workflow:report.error_title', { defaultValue: 'Could not load reports' })}
            description={t('workflow:report.error_description', {
              defaultValue: 'Something went wrong fetching run history. Try again.',
            })}
            className="w-[308px]"
          />
        </div>
      ) : displayed.length === 0 ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <IntuiEmptyState
            icon={<ClipboardList className="size-4 text-muted-foreground" />}
            title={t('workflow:report.empty_title', { defaultValue: 'No reports yet' })}
            description={t('workflow:report.empty_description', {
              defaultValue: 'Run the workflow to generate its first report',
            })}
            className="w-[308px]"
          />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* Putra ("10. Update" table callout): a bordered table is only for
              labels / embedded frames — a MAIN-page table renders open, with
              row dividers only (no 4-way outer border). */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('workflow:report.date', { defaultValue: 'Report date' })}</TableHead>
                <TableHead className="w-[160px]">{t('workflow:report.run_type', { defaultValue: 'Run type' })}</TableHead>
                <TableHead className="w-[160px]">{t('workflow:report.status', { defaultValue: 'Status' })}</TableHead>
                <TableHead className="w-[56px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayed.map((run) => (
                <TableRow
                  key={run.id}
                  role="button"
                  tabIndex={0}
                  aria-label={formatReportDate(run.startedAt)}
                  className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => setSelected(run)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setSelected(run)
                    }
                  }}
                >
                  <TableCell className="font-medium text-foreground">
                    {formatReportDate(run.startedAt)}
                  </TableCell>
                  <TableCell className="text-foreground">
                    {runTypeLabel(run) === 'Scheduled'
                      ? t('workflow:report.scheduled', { defaultValue: 'Scheduled' })
                      : t('workflow:report.manual', { defaultValue: 'Manual' })}
                  </TableCell>
                  <TableCell>
                    <StatusPill run={run} />
                  </TableCell>
                  <TableCell>
                    <ClipboardList className="size-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
